/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

// Strict migration of getAllFolderTypes from AngularJS to React TypeScript
// Logic, structure, and runtime behavior are strictly identical
export interface GetAllFolderTypesParams {
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | undefined
}

export interface FolderStructureTemplateType {
  id: number
  name: string
  description?: string
  system: boolean
  createdAt: string
  createdBy: {
    id: number
    name: string
  }
  updatedAt: string
  updatedBy: {
    id: number
    name: string
  }
}

export interface FolderStructureTemplateTypesResponse {
  elements: FolderStructureTemplateType[]
  totalElements: number
  totalPages: number
  pageNumber: number
  pageSize: number
}

export interface GetAllForTableParams {
  id?: number[]
  pageStart: number
  pageSize: number
  sortBy: string
  sortDesc: boolean
  _noLoadingSpinner?: boolean
  [key: string]: any
}

export const folderStructureTemplateTypesApiService = {
  getAllForTable: async (params: GetAllForTableParams): Promise<FolderStructureTemplateTypesResponse> => {
    return baseApiService.fetch('/api/v2/folderstructuretemplatetypes', {
      params
    })
  },

  getOptionsForAsyncPicker: async (
    search: string,
    pageStart: number
  ): Promise<{ options: Array<{ value: number; label: string }>; hasMore: boolean }> => {
    const params = {
      name: search || null,
      pageStart,
      pageSize: 25
    }

    const response = await baseApiService.fetch<FolderStructureTemplateTypesResponse>(
      '/api/v2/folderstructuretemplatetypes/picker',
      {
        method: 'GET',
        params
      }
    )

    const options = response.elements.map((item) => ({
      value: item.id,
      label: item.name
    }))

    // Sort options by label case-insensitively
    options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

    const hasMore = response.elements.length + pageStart < response.totalElements

    return { options, hasMore }
  },

  getById: async (typeId: number, hideLoader = false): Promise<FolderStructureTemplateType> => {
    return baseApiService.fetch(`/api/v2/folderstructuretemplatetypes/${typeId}`, {
      params: hideLoader ? { _noLoadingSpinner: true } : {}
    })
  },

  create: async (
    data: Omit<FolderStructureTemplateType, 'id' | 'system' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>,
    hideLoader = false
  ): Promise<FolderStructureTemplateType> => {
    return baseApiService.fetch('/api/folderstructuretemplatetypes', {
      method: 'POST',
      body: data,
      params: hideLoader ? { _noLoadingSpinner: true } : {}
    })
  },

  update: async (
    typeId: number,
    data: Partial<FolderStructureTemplateType>,
    hideLoader = false
  ): Promise<FolderStructureTemplateType> => {
    return baseApiService.fetch(`/api/folderstructuretemplatetypes/${typeId}`, {
      method: 'PUT',
      body: data,
      params: hideLoader ? { _noLoadingSpinner: true } : {}
    })
  },

  remove: async (typeId: number, hideLoader = false): Promise<void> => {
    return baseApiService.fetch(`/api/folderstructuretemplatetypes/${typeId}`, {
      method: 'DELETE',
      params: hideLoader ? { _noLoadingSpinner: true } : {}
    })
  }
}

export async function getAllFolderTypes(
  params?: GetAllFolderTypesParams
): Promise<FolderStructureTemplateTypesResponse> {
  // Default params as in legacy
  const queryParams = params ?? { sortBy: 'name', sortDesc: false }
  // Legacy uses apiWithLoader.one(baseURL).get(params)
  // baseURL is 'api/folderstructuretemplatetypes'
  return baseApiService.fetch<FolderStructureTemplateTypesResponse>('/api/folderstructuretemplatetypes', {
    params: queryParams
  })
}

// Legacy-compatible export
export const folderStructureTemplateTypes = {
  getAll: getAllFolderTypes
}
